import { selectUseMetric } from '../../state/dexfSettings/dexfSettingsSelectors';
import productService, { deriveMeasurements, getSections } from './index';
import { getMatchingConnections } from './models';
import { productMeasurementFormatter } from './productMeasurementsFormatter';
import {
  getProductsAsObject,
  getSmallestAndLargest,
} from '../../state/products/productsHelpers.ts';
import { DIMENSIONS } from '../../constants';
import { fractionLigatures } from '../../util/measures';
import { gatherArticleMeasurements } from './articleMeasurementsGatherer';

export const gatherProductMeasurement = (productIds, state) => {
  const allProducts = getProductsAsObject();
  const product = productIds[0];
  const productVariants = productIds
    .map(productId => allProducts[productId])
    .filter(Boolean);
  const useMetric = selectUseMetric(state);
  const measurementsVariants = deriveMeasurements(productVariants);
  const { articleArray, article, minMaxValues } =
    gatherArticleMeasurements(productIds);
  const getMeasurementType = useMetric ? 'textMetric' : 'textImperial';
  const isExtendable = productService.isExtendable(product);
  const ikeaMeasurements = {
    ...deriveMeasurements(articleArray),
    ...minMaxValues,
  };

  /**
   * If data from IKEA exists for a given dimension, this will be favored.
   * Else we use our own data
   *
   * @param dimension {string}
   * @returns {*}
   */
  const doMerge = dimension => {
    return ikeaMeasurements?.[dimension].length
      ? ikeaMeasurements[dimension]
      : measurementsVariants[dimension];
  };

  /**
   * Returns true if current product is a section and current dimension is width
   *
   * @param dimension
   * @returns {*|boolean}
   */
  const isTypeSectionAndDimensionWidth = dimension =>
    productService.isSection(product) && dimension === DIMENSIONS.width;

  /**
   * In case the current product is a section, it should be handled a differently
   *
   * @param dimension {string}
   * @returns {*}
   */
  const getCorrectMeasurements = dimension =>
    isTypeSectionAndDimensionWidth(dimension)
      ? measurementsVariants[DIMENSIONS.width]
      : doMerge(dimension);

  /**
   * Merges the two sets of data
   *
   * @returns {{}}
   */
  const mergeMeasurements = () =>
    Object.keys(DIMENSIONS).reduce(
      (acc, dimension) => ({
        ...acc,
        [dimension]: getCorrectMeasurements(dimension),
      }),
      {}
    );

  /**
   * Generates an array of dummy-tacs with each version of a section in it
   *
   * @returns {array}
   */
  const generateTacs = () =>
    productService.getSections().map(section => ({
      items: [
        {
          x: 0,
          y: 0,
          z: productService.getInitialZPos(section.id),
          ...section,
        },
      ],
    }));

  /**
   * Returns an array with which products fits in which sections
   */
  const calculateMatchingConnections = () =>
    productVariants
      .map(product =>
        generateTacs()
          .map(tac => getMatchingConnections(product, tac))
          .flat()
      )
      .flat();

  /**
   * Returns an object with info regarding which sections a product fits in
   */
  const getProductFit = () =>
    calculateMatchingConnections().filter(({ height }) => {
      const { max } = getSmallestAndLargest(measurementsVariants.height);
      return height >= max;
    });

  /**
   * Returns a boolean to tell if settings should be overridden
   *
   * @returns {boolean}
   */
  const forceDisplay = () => isExtendable;

  /**
   * Returns measurements string, either as is if the
   * product is extendable
   * or builds one
   *
   * @param fitsInProducts {array}
   * @param data
   * @returns {*}
   */
  const getMeasurementsString = (fitsInProducts, data) => {
    return forceDisplay()
      ? article.measureReference[getMeasurementType].replace(
          /\d+\/\d+/g,
          fraction => fractionLigatures[fraction]
        )
      : productMeasurementFormatter(
          product,
          deriveMeasurements(getSections()),
          deriveMeasurements(fitsInProducts),
          data,
          useMetric
        );
  };

  /**
   * Creates an object containing a product variants ID's, it's different dimension variants
   * which sections it will fit withing as well as which dimensions it will fit within
   *
   * @param fitsInProducts {array}
   * @returns {{fittingMeasurements: {depth: *[], width: *[], height: *[]}, measurementsVariants: *, products: *, fitsInProducts: *}}
   */
  const generateProductsMeasurementObject = fitsInProducts => ({
    ikeaMeasurements,
    measurementsVariants,
    fitsInProducts,
    products: productIds,
    fittingMeasurements: deriveMeasurements(fitsInProducts),
    forceDisplay: forceDisplay(),
    measurementString: getMeasurementsString(
      fitsInProducts,
      mergeMeasurements()
    ),
  });

  /**
   * Gets products measurements, and if measure is true, test products fit in all possible sections
   *
   * @param measure {boolean}
   * @param ids {array}
   * @returns {{fittingMeasurements: {depth: *[], width: *[], height: *[]}, measurementsVariants: *, products: *, fitsInProducts: *}}
   */
  return generateProductsMeasurementObject(getProductFit());
};
